import React, { FC } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import { HereMap, useHere } from "@/components/Here";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { IJobRequest } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents";

import "./_location-widget.scss";

interface LocationWidgetComponentProps {
  record: IJobRequest;
}

const LocationWidgetComponent: FC<LocationWidgetComponentProps> = ({ record }) => {
  const { t } = useTranslation("session");
  const hereLoaded = useHere();

  const coords = record.locations
    ?.map(location => location.coordinates)
    .filter(coord => coord?.lat && coord?.lon)
    .map(coord => ({ lat: coord!.lat.toString(), lng: coord!.lon.toString() }));

  if (!hereLoaded || !coords?.length) {
    return null;
  }

  const baseClass = "location-widget";

  return (
    <ResponsiveCard className={baseClass}>
      <Flex direction="column" innerMargin="md">
        <Header weight="bold" size="sm">
          {t("session:JOB_REQUESTS.DETAIL.LOCATION", { count: record.locations?.length })}
        </Header>

        <HereMap
          className={`${baseClass}__map`}
          markers={coords}
          hasMapSettings={false}
          hasScaleBar={false}
          hasZoom={false}
          zoom={record.locations && record.locations.length <= 1 ? 8 : undefined}
        />
      </Flex>
    </ResponsiveCard>
  );
};

interface LocationWidgetProps extends LocationWidgetComponentProps {}

export const LocationWidget: FC<LocationWidgetProps> = ({ ...restProps }) => {
  return (
    <ErrorBoundary fallback={<React.Fragment />}>
      <LocationWidgetComponent {...restProps} />
    </ErrorBoundary>
  );
};
